/**
 * TanStack Query hooks for Enrollment resource.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

export type EnrollmentStatus = 'active' | 'transferred' | 'completed' | 'dropped';

export interface Enrollment {
  id: string;
  studentId: string;
  studentCode?: string;
  studentName?: string;
  classId: string;
  classCode?: string;
  className?: string;
  branchId?: string;
  branchName?: string;
  status: EnrollmentStatus;
  enrolledAt?: string;
  completedAt?: string | null;
  droppedAt?: string | null;
  note?: string | null;
}

export interface ListEnrollmentsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  branchId?: string;
  classId?: string;
  studentId?: string;
  status?: EnrollmentStatus;
}

export const enrollmentKeys = {
  all: ['enrollments'] as const,
  list: (params?: ListEnrollmentsParams) => ['enrollments', 'list', params] as const,
  detail: (id: string) => ['enrollments', 'detail', id] as const,
};

export function useEnrollments(params?: ListEnrollmentsParams) {
  return useQuery({
    queryKey: enrollmentKeys.list(params),
    queryFn: () =>
      api.getPage<Enrollment>('/api/enrollments', params as Record<string, string>),
  });
}

export function useCreateEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<Enrollment>('/api/enrollments', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: enrollmentKeys.all });
    },
  });
}

export function useUpdateEnrollment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<Enrollment>(`/api/enrollments/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: enrollmentKeys.all });
    },
  });
}

export function useTransferEnrollment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) =>
      api.post<Enrollment>(`/api/enrollments/${id}/transfer`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: enrollmentKeys.all });
    },
  });
}
